class v {
  /**
   * @callback HookCallback
   * @this {*|Jsep} this
   * @param {Jsep} env
   * @returns: void
   */
  /**
   * Adds the given callback to the list of callbacks for the given hook.
   *
   * The callback will be invoked when the hook it is registered for is run.
   *
   * One callback function can be registered to multiple hooks and the same hook multiple times.
   *
   * @param {string|object} name The name of the hook, or an object of callbacks keyed by name
   * @param {HookCallback|boolean} callback The callback function which is given environment variables.
   * @param {?boolean} [first=false] Will add the hook to the top of the list (defaults to the bottom)
   * @public
   */
  add(t, r, n) {
    if (typeof arguments[0] != "string")
      for (let i in arguments[0])
        this.add(i, arguments[0][i], arguments[1]);
    else
      (Array.isArray(t) ? t : [t]).forEach(function(i) {
        this[i] = this[i] || [], r && this[i][n ? "unshift" : "push"](r);
      }, this);
  }
  /**
   * Runs a hook invoking all registered callbacks with the given environment variables.
   *
   * Callbacks will be invoked synchronously and in the order in which they were registered.
   *
   * @param {string} name The name of the hook.
   * @param {Object<string, any>} env The environment variables of the hook passed to all callbacks registered.
   * @public
   */
  run(t, r) {
    this[t] = this[t] || [], this[t].forEach(function(n) {
      n.call(r && r.context ? r.context : r, r);
    });
  }
}
class M {
  constructor(t) {
    this.jsep = t, this.registered = {};
  }
  /**
   * @callback PluginSetup
   * @this {Jsep} jsep
   * @returns: void
   */
  /**
   * Adds the given plugin(s) to the registry
   *
   * @param {object} plugins
   * @param {string} plugins.name The name of the plugin
   * @param {PluginSetup} plugins.init The init function
   * @public
   */
  register() {
    for (var t = arguments.length, r = new Array(t), n = 0; n < t; n++)
      r[n] = arguments[n];
    r.forEach((i) => {
      if (typeof i != "object" || !i.name || !i.init)
        throw new Error("Invalid JSEP plugin format");
      this.registered[i.name] || (i.init(this.jsep), this.registered[i.name] = i);
    });
  }
}
class s {
  /**
   * @returns {string}
   */
  static get version() {
    return "1.3.9";
  }
  /**
   * @returns {string}
   */
  static toString() {
    return "JavaScript Expression Parser (JSEP) v" + s.version;
  }
  // ==================== CONFIG ================================
  /**
   * @method addUnaryOp
   * @param {string} op_name The name of the unary op to add
   * @returns {Jsep}
   */
  static addUnaryOp(t) {
    return s.max_unop_len = Math.max(t.length, s.max_unop_len), s.unary_ops[t] = 1, s;
  }
  /**
   * @method jsep.addBinaryOp
   * @param {string} op_name The name of the binary op to add
   * @param {number} precedence The precedence of the binary op (can be a float). Higher number = higher precedence
   * @param {boolean} [isRightAssociative=false] whether operator is right-associative
   * @returns {Jsep}
   */
  static addBinaryOp(t, r, n) {
    return s.max_binop_len = Math.max(t.length, s.max_binop_len), s.binary_ops[t] = r, n ? s.right_associative.add(t) : s.right_associative.delete(t), s;
  }
  /**
   * @method addIdentifierChar
   * @param {string} char The additional character to treat as a valid part of an identifier
   * @returns {Jsep}
   */
  static addIdentifierChar(t) {
    return s.additional_identifier_chars.add(t), s;
  }
  /**
   * @method addLiteral
   * @param {string} literal_name The name of the literal to add
   * @param {*} literal_value The value of the literal
   * @returns {Jsep}
   */
  static addLiteral(t, r) {
    return s.literals[t] = r, s;
  }
  /**
   * @method removeUnaryOp
   * @param {string} op_name The name of the unary op to remove
   * @returns {Jsep}
   */
  static removeUnaryOp(t) {
    return delete s.unary_ops[t], t.length === s.max_unop_len && (s.max_unop_len = s.getMaxKeyLen(s.unary_ops)), s;
  }
  /**
   * @method removeAllUnaryOps
   * @returns {Jsep}
   */
  static removeAllUnaryOps() {
    return s.unary_ops = {}, s.max_unop_len = 0, s;
  }
  /**
   * @method removeIdentifierChar
   * @param {string} char The additional character to stop treating as a valid part of an identifier
   * @returns {Jsep}
   */
  static removeIdentifierChar(t) {
    return s.additional_identifier_chars.delete(t), s;
  }
  /**
   * @method removeBinaryOp
   * @param {string} op_name The name of the binary op to remove
   * @returns {Jsep}
   */
  static removeBinaryOp(t) {
    return delete s.binary_ops[t], t.length === s.max_binop_len && (s.max_binop_len = s.getMaxKeyLen(s.binary_ops)), s.right_associative.delete(t), s;
  }
  /**
   * @method removeAllBinaryOps
   * @returns {Jsep}
   */
  static removeAllBinaryOps() {
    return s.binary_ops = {}, s.max_binop_len = 0, s;
  }
  /**
   * @method removeLiteral
   * @param {string} literal_name The name of the literal to remove
   * @returns {Jsep}
   */
  static removeLiteral(t) {
    return delete s.literals[t], s;
  }
  /**
   * @method removeAllLiterals
   * @returns {Jsep}
   */
  static removeAllLiterals() {
    return s.literals = {}, s;
  }
  // ==================== END CONFIG ============================
  /**
   * @returns {string}
   */
  get char() {
    return this.expr.charAt(this.index);
  }
  /**
   * @returns {number}
   */
  get code() {
    return this.expr.charCodeAt(this.index);
  }
  /**
   * @param {string} expr a string with the passed in express
   * @returns Jsep
   */
  constructor(t) {
    this.expr = t, this.index = 0;
  }
  /**
   * static top-level parser
   * @returns {jsep.Expression}
   */
  static parse(t) {
    return new s(t).parse();
  }
  /**
   * Get the longest key length of any object
   * @param {object} obj
   * @returns {number}
   */
  static getMaxKeyLen(t) {
    return Math.max(0, ...Object.keys(t).map((r) => r.length));
  }
  /**
   * `ch` is a character code in the next three functions
   * @param {number} ch
   * @returns {boolean}
   */
  static isDecimalDigit(t) {
    return t >= 48 && t <= 57;
  }
  /**
   * Returns the precedence of a binary operator or `0` if it isn't a binary operator. Can be float.
   * @param {string} op_val
   * @returns {number}
   */
  static binaryPrecedence(t) {
    return s.binary_ops[t] || 0;
  }
  /**
   * Looks for start of identifier
   * @param {number} ch
   * @returns {boolean}
   */
  static isIdentifierStart(t) {
    return t >= 65 && t <= 90 || // A...Z
    t >= 97 && t <= 122 || // a...z
    t >= 128 && !s.binary_ops[String.fromCharCode(t)] || // any non-ASCII that is not an operator
    s.additional_identifier_chars.has(String.fromCharCode(t));
  }
  /**
   * @param {number} ch
   * @returns {boolean}
   */
  static isIdentifierPart(t) {
    return s.isIdentifierStart(t) || s.isDecimalDigit(t);
  }
  /**
   * throw error at index of the expression
   * @param {string} message
   * @throws
   */
  throwError(t) {
    const r = new Error(t + " at character " + this.index);
    throw r.index = this.index, r.description = t, r;
  }
  /**
   * Run a given hook
   * @param {string} name
   * @param {jsep.Expression|false} [node]
   * @returns {?jsep.Expression}
   */
  runHook(t, r) {
    if (s.hooks[t]) {
      const n = {
        context: this,
        node: r
      };
      return s.hooks.run(t, n), n.node;
    }
    return r;
  }
  /**
   * Runs a given hook until one returns a node
   * @param {string} name
   * @returns {?jsep.Expression}
   */
  searchHook(t) {
    if (s.hooks[t]) {
      const r = {
        context: this
      };
      return s.hooks[t].find(function(n) {
        return n.call(r.context, r), r.node;
      }), r.node;
    }
  }
  /**
   * Push `index` up to the next non-space character
   */
  gobbleSpaces() {
    let t = this.code;
    for (; t === s.SPACE_CODE || t === s.TAB_CODE || t === s.LF_CODE || t === s.CR_CODE; )
      t = this.expr.charCodeAt(++this.index);
    this.runHook("gobble-spaces");
  }
  /**
   * Top-level method to parse all expressions and returns compound or single node
   * @returns {jsep.Expression}
   */
  parse() {
    this.runHook("before-all");
    const t = this.gobbleExpressions(), r = t.length === 1 ? t[0] : {
      type: s.COMPOUND,
      body: t
    };
    return this.runHook("after-all", r);
  }
  /**
   * top-level parser (but can be reused within as well)
   * @param {number} [untilICode]
   * @returns {jsep.Expression[]}
   */
  gobbleExpressions(t) {
    let r = [], n, i;
    for (; this.index < this.expr.length; )
      if (n = this.code, n === s.SEMCOL_CODE || n === s.COMMA_CODE)
        this.index++;
      else if (i = this.gobbleExpression())
        r.push(i);
      else if (this.index < this.expr.length) {
        if (n === t)
          break;
        this.throwError('Unexpected "' + this.char + '"');
      }
    return r;
  }
  /**
   * The main parsing function.
   * @returns {?jsep.Expression}
   */
  gobbleExpression() {
    const t = this.searchHook("gobble-expression") || this.gobbleBinaryExpression();
    return this.gobbleSpaces(), this.runHook("after-expression", t);
  }
  /**
   * Search for the operation portion of the string (e.g. `+`, `===`)
   * Start by taking the longest possible binary operations (3 characters: `===`, `!==`, `>>>`)
   * and move down from 3 to 2 to 1 character until a matching binary operation is found
   * then, return that binary operation
   * @returns {string|boolean}
   */
  gobbleBinaryOp() {
    this.gobbleSpaces();
    let t = this.expr.substr(this.index, s.max_binop_len), r = t.length;
    for (; r > 0; ) {
      if (s.binary_ops.hasOwnProperty(t) && (!s.isIdentifierStart(this.code) || this.index + t.length < this.expr.length && !s.isIdentifierPart(this.expr.charCodeAt(this.index + t.length))))
        return this.index += r, t;
      t = t.substr(0, --r);
    }
    return !1;
  }
  /**
   * This function is responsible for gobbling an individual expression,
   * e.g. `1`, `1+2`, `a+(b*2)-Math.sqrt(2)`
   * @returns {?jsep.BinaryExpression}
   */
  gobbleBinaryExpression() {
    let t, r, n, i, o, a, c, l, h;
    if (a = this.gobbleToken(), !a || (r = this.gobbleBinaryOp(), !r))
      return a;
    for (o = {
      value: r,
      prec: s.binaryPrecedence(r),
      right_a: s.right_associative.has(r)
    }, c = this.gobbleToken(), c || this.throwError("Expected expression after " + r), i = [a, o, c]; r = this.gobbleBinaryOp(); ) {
      if (n = s.binaryPrecedence(r), n === 0) {
        this.index -= r.length;
        break;
      }
      o = {
        value: r,
        prec: n,
        right_a: s.right_associative.has(r)
      }, h = r;
      const E = (p) => o.right_a && p.right_a ? n > p.prec : n <= p.prec;
      for (; i.length > 2 && E(i[i.length - 2]); )
        c = i.pop(), r = i.pop().value, a = i.pop(), t = {
          type: s.BINARY_EXP,
          operator: r,
          left: a,
          right: c
        }, i.push(t);
      t = this.gobbleToken(), t || this.throwError("Expected expression after " + h), i.push(o, t);
    }
    for (l = i.length - 1, t = i[l]; l > 1; )
      t = {
        type: s.BINARY_EXP,
        operator: i[l - 1].value,
        left: i[l - 2],
        right: t
      }, l -= 2;
    return t;
  }
  /**
   * An individual part of a binary expression:
   * e.g. `foo.bar(baz)`, `1`, `"abc"`, `(a % 2)` (because it's in parenthesis)
   * @returns {boolean|jsep.Expression}
   */
  gobbleToken() {
    let t, r, n, i;
    if (this.gobbleSpaces(), i = this.searchHook("gobble-token"), i)
      return this.runHook("after-token", i);
    if (t = this.code, s.isDecimalDigit(t) || t === s.PERIOD_CODE)
      return this.gobbleNumericLiteral();
    if (t === s.SQUOTE_CODE || t === s.DQUOTE_CODE)
      i = this.gobbleStringLiteral();
    else if (t === s.OBRACK_CODE)
      i = this.gobbleArray();
    else {
      for (r = this.expr.substr(this.index, s.max_unop_len), n = r.length; n > 0; ) {
        if (s.unary_ops.hasOwnProperty(r) && (!s.isIdentifierStart(this.code) || this.index + r.length < this.expr.length && !s.isIdentifierPart(this.expr.charCodeAt(this.index + r.length)))) {
          this.index += n;
          const o = this.gobbleToken();
          return o || this.throwError("missing unaryOp argument"), this.runHook("after-token", {
            type: s.UNARY_EXP,
            operator: r,
            argument: o,
            prefix: !0
          });
        }
        r = r.substr(0, --n);
      }
      s.isIdentifierStart(t) ? (i = this.gobbleIdentifier(), s.literals.hasOwnProperty(i.name) ? i = {
        type: s.LITERAL,
        value: s.literals[i.name],
        raw: i.name
      } : i.name === s.this_str && (i = {
        type: s.THIS_EXP
      })) : t === s.OPAREN_CODE && (i = this.gobbleGroup());
    }
    return i ? (i = this.gobbleTokenProperty(i), this.runHook("after-token", i)) : this.runHook("after-token", !1);
  }
  /**
   * Gobble properties of of identifiers/strings/arrays/groups.
   * e.g. `foo`, `bar.baz`, `foo['bar'].baz`
   * It also gobbles function calls:
   * e.g. `Math.acos(obj.angle)`
   * @param {jsep.Expression} node
   * @returns {jsep.Expression}
   */
  gobbleTokenProperty(t) {
    this.gobbleSpaces();
    let r = this.code;
    for (; r === s.PERIOD_CODE || r === s.OBRACK_CODE || r === s.OPAREN_CODE || r === s.QUMARK_CODE; ) {
      let n;
      if (r === s.QUMARK_CODE) {
        if (this.expr.charCodeAt(this.index + 1) !== s.PERIOD_CODE)
          break;
        n = !0, this.index += 2, this.gobbleSpaces(), r = this.code;
      }
      this.index++, r === s.OBRACK_CODE ? (t = {
        type: s.MEMBER_EXP,
        computed: !0,
        object: t,
        property: this.gobbleExpression()
      }, t.property || this.throwError('Unexpected "' + this.char + '"'), this.gobbleSpaces(), r = this.code, r !== s.CBRACK_CODE && this.throwError("Unclosed ["), this.index++) : r === s.OPAREN_CODE ? t = {
        type: s.CALL_EXP,
        arguments: this.gobbleArguments(s.CPAREN_CODE),
        callee: t
      } : (r === s.PERIOD_CODE || n) && (n && this.index--, this.gobbleSpaces(), t = {
        type: s.MEMBER_EXP,
        computed: !1,
        object: t,
        property: this.gobbleIdentifier()
      }), n && (t.optional = !0), this.gobbleSpaces(), r = this.code;
    }
    return t;
  }
  /**
   * Parse simple numeric literals: `12`, `3.4`, `.5`. Do this by using a string to
   * keep track of everything in the numeric literal and then calling `parseFloat` on that string
   * @returns {jsep.Literal}
   */
  gobbleNumericLiteral() {
    let t = "", r, n;
    for (; s.isDecimalDigit(this.code); )
      t += this.expr.charAt(this.index++);
    if (this.code === s.PERIOD_CODE)
      for (t += this.expr.charAt(this.index++); s.isDecimalDigit(this.code); )
        t += this.expr.charAt(this.index++);
    if (r = this.char, r === "e" || r === "E") {
      for (t += this.expr.charAt(this.index++), r = this.char, (r === "+" || r === "-") && (t += this.expr.charAt(this.index++)); s.isDecimalDigit(this.code); )
        t += this.expr.charAt(this.index++);
      s.isDecimalDigit(this.expr.charCodeAt(this.index - 1)) || this.throwError("Expected exponent (" + t + this.char + ")");
    }
    return n = this.code, s.isIdentifierStart(n) ? this.throwError("Variable names cannot start with a number (" + t + this.char + ")") : (n === s.PERIOD_CODE || t.length === 1 && t.charCodeAt(0) === s.PERIOD_CODE) && this.throwError("Unexpected period"), {
      type: s.LITERAL,
      value: parseFloat(t),
      raw: t
    };
  }
  /**
   * Parses a string literal, staring with single or double quotes with basic support for escape codes
   * e.g. `"hello world"`, `'this is\nJSEP'`
   * @returns {jsep.Literal}
   */
  gobbleStringLiteral() {
    let t = "";
    const r = this.index, n = this.expr.charAt(this.index++);
    let i = !1;
    for (; this.index < this.expr.length; ) {
      let o = this.expr.charAt(this.index++);
      if (o === n) {
        i = !0;
        break;
      } else if (o === "\\")
        switch (o = this.expr.charAt(this.index++), o) {
          case "n":
            t += `
`;
            break;
          case "r":
            t += "\r";
            break;
          case "t":
            t += "	";
            break;
          case "b":
            t += "\b";
            break;
          case "f":
            t += "\f";
            break;
          case "v":
            t += "\v";
            break;
          default:
            t += o;
        }
      else
        t += o;
    }
    return i || this.throwError('Unclosed quote after "' + t + '"'), {
      type: s.LITERAL,
      value: t,
      raw: this.expr.substring(r, this.index)
    };
  }
  /**
   * Gobbles only identifiers
   * e.g.: `foo`, `_value`, `$x1`
   * Also, this function checks if that identifier is a literal:
   * (e.g. `true`, `false`, `null`) or `this`
   * @returns {jsep.Identifier}
   */
  gobbleIdentifier() {
    let t = this.code, r = this.index;
    for (s.isIdentifierStart(t) ? this.index++ : this.throwError("Unexpected " + this.char); this.index < this.expr.length && (t = this.code, s.isIdentifierPart(t)); )
      this.index++;
    return {
      type: s.IDENTIFIER,
      name: this.expr.slice(r, this.index)
    };
  }
  /**
   * Gobbles a list of arguments within the context of a function call
   * or array literal. This function also assumes that the opening character
   * `(` or `[` has already been gobbled, and gobbles expressions and commas
   * until the terminator character `)` or `]` is encountered.
   * e.g. `foo(bar, baz)`, `my_func()`, or `[bar, baz]`
   * @param {number} termination
   * @returns {jsep.Expression[]}
   */
  gobbleArguments(t) {
    const r = [];
    let n = !1, i = 0;
    for (; this.index < this.expr.length; ) {
      this.gobbleSpaces();
      let o = this.code;
      if (o === t) {
        n = !0, this.index++, t === s.CPAREN_CODE && i && i >= r.length && this.throwError("Unexpected token " + String.fromCharCode(t));
        break;
      } else if (o === s.COMMA_CODE) {
        if (this.index++, i++, i !== r.length) {
          if (t === s.CPAREN_CODE)
            this.throwError("Unexpected token ,");
          else if (t === s.CBRACK_CODE)
            for (let a = r.length; a < i; a++)
              r.push(null);
        }
      } else if (r.length !== i && i !== 0)
        this.throwError("Expected comma");
      else {
        const a = this.gobbleExpression();
        (!a || a.type === s.COMPOUND) && this.throwError("Expected comma"), r.push(a);
      }
    }
    return n || this.throwError("Expected " + String.fromCharCode(t)), r;
  }
  /**
   * Responsible for parsing a group of things within parentheses `()`
   * that have no identifier in front (so not a function call)
   * This function assumes that it needs to gobble the opening parenthesis
   * and then tries to gobble everything within that parenthesis, assuming
   * that the next thing it should see is the close parenthesis. If not,
   * then the expression probably doesn't have a `)`
   * @returns {boolean|jsep.Expression}
   */
  gobbleGroup() {
    this.index++;
    let t = this.gobbleExpressions(s.CPAREN_CODE);
    if (this.code === s.CPAREN_CODE)
      return this.index++, t.length === 1 ? t[0] : t.length ? {
        type: s.SEQUENCE_EXP,
        expressions: t
      } : !1;
    this.throwError("Unclosed (");
  }
  /**
   * Responsible for parsing Array literals `[1, 2, 3]`
   * This function assumes that it needs to gobble the opening bracket
   * and then tries to gobble the expressions as arguments.
   * @returns {jsep.ArrayExpression}
   */
  gobbleArray() {
    return this.index++, {
      type: s.ARRAY_EXP,
      elements: this.gobbleArguments(s.CBRACK_CODE)
    };
  }
}
const k = new v();
Object.assign(s, {
  hooks: k,
  plugins: new M(s),
  // Node Types
  // ----------
  // This is the full set of types that any JSEP node can be.
  // Store them here to save space when minified
  COMPOUND: "Compound",
  SEQUENCE_EXP: "SequenceExpression",
  IDENTIFIER: "Identifier",
  MEMBER_EXP: "MemberExpression",
  LITERAL: "Literal",
  THIS_EXP: "ThisExpression",
  CALL_EXP: "CallExpression",
  UNARY_EXP: "UnaryExpression",
  BINARY_EXP: "BinaryExpression",
  ARRAY_EXP: "ArrayExpression",
  TAB_CODE: 9,
  LF_CODE: 10,
  CR_CODE: 13,
  SPACE_CODE: 32,
  PERIOD_CODE: 46,
  // '.'
  COMMA_CODE: 44,
  // ','
  SQUOTE_CODE: 39,
  // single quote
  DQUOTE_CODE: 34,
  // double quotes
  OPAREN_CODE: 40,
  // (
  CPAREN_CODE: 41,
  // )
  OBRACK_CODE: 91,
  // [
  CBRACK_CODE: 93,
  // ]
  QUMARK_CODE: 63,
  // ?
  SEMCOL_CODE: 59,
  // ;
  COLON_CODE: 58,
  // :
  // Operations
  // ----------
  // Use a quickly-accessible map to store all of the unary operators
  // Values are set to `1` (it really doesn't matter)
  unary_ops: {
    "-": 1,
    "!": 1,
    "~": 1,
    "+": 1
  },
  // Also use a map for the binary operations but set their values to their
  // binary precedence for quick reference (higher number = higher precedence)
  // see [Order of operations](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Operator_Precedence)
  binary_ops: {
    "||": 1,
    "&&": 2,
    "|": 3,
    "^": 4,
    "&": 5,
    "==": 6,
    "!=": 6,
    "===": 6,
    "!==": 6,
    "<": 7,
    ">": 7,
    "<=": 7,
    ">=": 7,
    "<<": 8,
    ">>": 8,
    ">>>": 8,
    "+": 9,
    "-": 9,
    "*": 10,
    "/": 10,
    "%": 10
  },
  // sets specific binary_ops as right-associative
  right_associative: /* @__PURE__ */ new Set(),
  // Additional valid identifier chars, apart from a-z, A-Z and 0-9 (except on the starting char)
  additional_identifier_chars: /* @__PURE__ */ new Set(["$", "_"]),
  // Literals
  // ----------
  // Store the values to return for the various literals we may encounter
  literals: {
    true: !0,
    false: !1,
    null: null
  },
  // Except for `this`, which is special. This could be changed to something like `'self'` as well
  this_str: "this"
});
s.max_unop_len = s.getMaxKeyLen(s.unary_ops);
s.max_binop_len = s.getMaxKeyLen(s.binary_ops);
const C = (e) => new s(e).parse(), $ = Object.getOwnPropertyNames(class {
});
Object.getOwnPropertyNames(s).filter((e) => !$.includes(e) && C[e] === void 0).forEach((e) => {
  C[e] = s[e];
});
C.Jsep = s;
const B = "ConditionalExpression";
var j = {
  name: "ternary",
  init(e) {
    e.hooks.add("after-expression", function(r) {
      if (r.node && this.code === e.QUMARK_CODE) {
        this.index++;
        const n = r.node, i = this.gobbleExpression();
        if (i || this.throwError("Expected expression"), this.gobbleSpaces(), this.code === e.COLON_CODE) {
          this.index++;
          const o = this.gobbleExpression();
          if (o || this.throwError("Expected expression"), r.node = {
            type: B,
            test: n,
            consequent: i,
            alternate: o
          }, n.operator && e.binary_ops[n.operator] <= 0.9) {
            let a = n;
            for (; a.right.operator && e.binary_ops[a.right.operator] <= 0.9; )
              a = a.right;
            r.node.test = a.right, a.right = r.node, r.node = n;
          }
        } else
          this.throwError("Expected :");
      }
    });
  }
};
C.plugins.register(j);
const m = 47, F = 92;
var V = {
  name: "regex",
  init(e) {
    e.hooks.add("gobble-token", function(r) {
      if (this.code === m) {
        const n = ++this.index;
        let i = !1;
        for (; this.index < this.expr.length; ) {
          if (this.code === m && !i) {
            const o = this.expr.slice(n, this.index);
            let a = "";
            for (; ++this.index < this.expr.length; ) {
              const l = this.code;
              if (l >= 97 && l <= 122 || l >= 65 && l <= 90 || l >= 48 && l <= 57)
                a += this.char;
              else
                break;
            }
            let c;
            try {
              c = new RegExp(o, a);
            } catch (l) {
              this.throwError(l.message);
            }
            return r.node = {
              type: e.LITERAL,
              value: c,
              raw: this.expr.slice(n - 1, this.index)
            }, r.node = this.gobbleTokenProperty(r.node), r.node;
          }
          this.code === e.OBRACK_CODE ? i = !0 : i && this.code === e.CBRACK_CODE && (i = !1), this.index += this.code === F ? 2 : 1;
        }
        this.throwError("Unclosed Regex");
      }
    });
  }
};
const w = 43, X = 45, x = {
  name: "assignment",
  assignmentOperators: /* @__PURE__ */ new Set(["=", "*=", "**=", "/=", "%=", "+=", "-=", "<<=", ">>=", ">>>=", "&=", "^=", "|="]),
  updateOperators: [w, X],
  assignmentPrecedence: 0.9,
  init(e) {
    const t = [e.IDENTIFIER, e.MEMBER_EXP];
    x.assignmentOperators.forEach((n) => e.addBinaryOp(n, x.assignmentPrecedence, !0)), e.hooks.add("gobble-token", function(i) {
      const o = this.code;
      x.updateOperators.some((a) => a === o && a === this.expr.charCodeAt(this.index + 1)) && (this.index += 2, i.node = {
        type: "UpdateExpression",
        operator: o === w ? "++" : "--",
        argument: this.gobbleTokenProperty(this.gobbleIdentifier()),
        prefix: !0
      }, (!i.node.argument || !t.includes(i.node.argument.type)) && this.throwError(`Unexpected ${i.node.operator}`));
    }), e.hooks.add("after-token", function(i) {
      if (i.node) {
        const o = this.code;
        x.updateOperators.some((a) => a === o && a === this.expr.charCodeAt(this.index + 1)) && (t.includes(i.node.type) || this.throwError(`Unexpected ${i.node.operator}`), this.index += 2, i.node = {
          type: "UpdateExpression",
          operator: o === w ? "++" : "--",
          argument: i.node,
          prefix: !1
        });
      }
    }), e.hooks.add("after-expression", function(i) {
      i.node && r(i.node);
    });
    function r(n) {
      x.assignmentOperators.has(n.operator) ? (n.type = "AssignmentExpression", r(n.left), r(n.right)) : n.operator || Object.values(n).forEach((i) => {
        i && typeof i == "object" && r(i);
      });
    }
  }
};
C.plugins.register(V, x);
const d = {
  /**
   * @param {jsep.Expression} ast
   * @param {Record<string, any>} subs
   */
  evalAst(e, t) {
    switch (e.type) {
      case "BinaryExpression":
      case "LogicalExpression":
        return d.evalBinaryExpression(e, t);
      case "Compound":
        return d.evalCompound(e, t);
      case "ConditionalExpression":
        return d.evalConditionalExpression(e, t);
      case "Identifier":
        return d.evalIdentifier(e, t);
      case "Literal":
        return d.evalLiteral(e, t);
      case "MemberExpression":
        return d.evalMemberExpression(e, t);
      case "UnaryExpression":
        return d.evalUnaryExpression(e, t);
      case "ArrayExpression":
        return d.evalArrayExpression(e, t);
      case "CallExpression":
        return d.evalCallExpression(e, t);
      case "AssignmentExpression":
        return d.evalAssignmentExpression(e, t);
      default:
        throw SyntaxError("Unexpected expression", e);
    }
  },
  evalBinaryExpression(e, t) {
    return {
      "||": (n, i) => n || i(),
      "&&": (n, i) => n && i(),
      "|": (n, i) => n | i(),
      "^": (n, i) => n ^ i(),
      "&": (n, i) => n & i(),
      // eslint-disable-next-line eqeqeq -- API
      "==": (n, i) => n == i(),
      // eslint-disable-next-line eqeqeq -- API
      "!=": (n, i) => n != i(),
      "===": (n, i) => n === i(),
      "!==": (n, i) => n !== i(),
      "<": (n, i) => n < i(),
      ">": (n, i) => n > i(),
      "<=": (n, i) => n <= i(),
      ">=": (n, i) => n >= i(),
      "<<": (n, i) => n << i(),
      ">>": (n, i) => n >> i(),
      ">>>": (n, i) => n >>> i(),
      "+": (n, i) => n + i(),
      "-": (n, i) => n - i(),
      "*": (n, i) => n * i(),
      "/": (n, i) => n / i(),
      "%": (n, i) => n % i()
    }[e.operator](d.evalAst(e.left, t), () => d.evalAst(e.right, t));
  },
  evalCompound(e, t) {
    let r;
    for (let n = 0; n < e.body.length; n++) {
      e.body[n].type === "Identifier" && ["var", "let", "const"].includes(e.body[n].name) && e.body[n + 1] && e.body[n + 1].type === "AssignmentExpression" && (n += 1);
      const i = e.body[n];
      r = d.evalAst(i, t);
    }
    return r;
  },
  evalConditionalExpression(e, t) {
    return d.evalAst(e.test, t) ? d.evalAst(e.consequent, t) : d.evalAst(e.alternate, t);
  },
  evalIdentifier(e, t) {
    if (e.name in t)
      return t[e.name];
    throw ReferenceError(`${e.name} is not defined`);
  },
  evalLiteral(e) {
    return e.value;
  },
  evalMemberExpression(e, t) {
    const r = e.computed ? d.evalAst(e.property) : e.property.name, n = d.evalAst(e.object, t), i = n[r];
    return typeof i == "function" ? i === Function ? i : i.bind(n) : i;
  },
  evalUnaryExpression(e, t) {
    return {
      "-": (n) => -d.evalAst(n, t),
      "!": (n) => !d.evalAst(n, t),
      "~": (n) => ~d.evalAst(n, t),
      // eslint-disable-next-line no-implicit-coercion -- API
      "+": (n) => +d.evalAst(n, t)
    }[e.operator](e.argument);
  },
  evalArrayExpression(e, t) {
    return e.elements.map((r) => d.evalAst(r, t));
  },
  evalCallExpression(e, t) {
    const r = e.arguments.map((i) => d.evalAst(i, t)), n = d.evalAst(e.callee, t);
    if (n === Function)
      throw new Error("Function constructor is disabled");
    return n(...r);
  },
  evalAssignmentExpression(e, t) {
    if (e.left.type !== "Identifier")
      throw SyntaxError("Invalid left-hand side in assignment");
    const r = e.left.name, n = d.evalAst(e.right, t);
    return t[r] = n, t[r];
  }
};
class G {
  /**
   * @param {string} expr Expression to evaluate
   */
  constructor(t) {
    this.code = t, this.ast = C(this.code);
  }
  /**
   * @param {object} context Object whose items will be added
   *   to evaluation
   * @returns {EvaluatedResult} Result of evaluated code
   */
  runInNewContext(t) {
    const r = {
      ...t
    };
    return d.evalAst(this.ast, r);
  }
}
function A(e, t) {
  return e = e.slice(), e.push(t), e;
}
function R(e, t) {
  return t = t.slice(), t.unshift(e), t;
}
class K extends Error {
  /**
   * @param {AnyResult} value The evaluated scalar value
   */
  constructor(t) {
    super('JSONPath should not be called with "new" (it prevents return of (unwrapped) scalar values)'), this.avoidNew = !0, this.value = t, this.name = "NewError";
  }
}
function f(e, t, r, n, i) {
  if (!(this instanceof f))
    try {
      return new f(e, t, r, n, i);
    } catch (a) {
      if (!a.avoidNew)
        throw a;
      return a.value;
    }
  typeof e == "string" && (i = n, n = r, r = t, t = e, e = null);
  const o = e && typeof e == "object";
  if (e = e || {}, this.json = e.json || r, this.path = e.path || t, this.resultType = e.resultType || "value", this.flatten = e.flatten || !1, this.wrap = Object.hasOwn(e, "wrap") ? e.wrap : !0, this.sandbox = e.sandbox || {}, this.eval = e.eval === void 0 ? "safe" : e.eval, this.ignoreEvalErrors = typeof e.ignoreEvalErrors > "u" ? !1 : e.ignoreEvalErrors, this.parent = e.parent || null, this.parentProperty = e.parentProperty || null, this.callback = e.callback || n || null, this.otherTypeCallback = e.otherTypeCallback || i || function() {
    throw new TypeError("You must supply an otherTypeCallback callback option with the @other() operator.");
  }, e.autostart !== !1) {
    const a = {
      path: o ? e.path : t
    };
    o ? "json" in e && (a.json = e.json) : a.json = r;
    const c = this.evaluate(a);
    if (!c || typeof c != "object")
      throw new K(c);
    return c;
  }
}
f.prototype.evaluate = function(e, t, r, n) {
  let i = this.parent, o = this.parentProperty, {
    flatten: a,
    wrap: c
  } = this;
  if (this.currResultType = this.resultType, this.currEval = this.eval, this.currSandbox = this.sandbox, r = r || this.callback, this.currOtherTypeCallback = n || this.otherTypeCallback, t = t || this.json, e = e || this.path, e && typeof e == "object" && !Array.isArray(e)) {
    if (!e.path && e.path !== "")
      throw new TypeError('You must supply a "path" property when providing an object argument to JSONPath.evaluate().');
    if (!Object.hasOwn(e, "json"))
      throw new TypeError('You must supply a "json" property when providing an object argument to JSONPath.evaluate().');
    ({
      json: t
    } = e), a = Object.hasOwn(e, "flatten") ? e.flatten : a, this.currResultType = Object.hasOwn(e, "resultType") ? e.resultType : this.currResultType, this.currSandbox = Object.hasOwn(e, "sandbox") ? e.sandbox : this.currSandbox, c = Object.hasOwn(e, "wrap") ? e.wrap : c, this.currEval = Object.hasOwn(e, "eval") ? e.eval : this.currEval, r = Object.hasOwn(e, "callback") ? e.callback : r, this.currOtherTypeCallback = Object.hasOwn(e, "otherTypeCallback") ? e.otherTypeCallback : this.currOtherTypeCallback, i = Object.hasOwn(e, "parent") ? e.parent : i, o = Object.hasOwn(e, "parentProperty") ? e.parentProperty : o, e = e.path;
  }
  if (i = i || null, o = o || null, Array.isArray(e) && (e = f.toPathString(e)), !e && e !== "" || !t)
    return;
  const l = f.toPathArray(e);
  l[0] === "$" && l.length > 1 && l.shift(), this._hasParentSelector = null;
  const h = this._trace(l, t, ["$"], i, o, r).filter(function(E) {
    return E && !E.isParentSelector;
  });
  return h.length ? !c && h.length === 1 && !h[0].hasArrExpr ? this._getPreferredOutput(h[0]) : h.reduce((E, p) => {
    const g = this._getPreferredOutput(p);
    return a && Array.isArray(g) ? E = E.concat(g) : E.push(g), E;
  }, []) : c ? [] : void 0;
};
f.prototype._getPreferredOutput = function(e) {
  const t = this.currResultType;
  switch (t) {
    case "all": {
      const r = Array.isArray(e.path) ? e.path : f.toPathArray(e.path);
      return e.pointer = f.toPointer(r), e.path = typeof e.path == "string" ? e.path : f.toPathString(e.path), e;
    }
    case "value":
    case "parent":
    case "parentProperty":
      return e[t];
    case "path":
      return f.toPathString(e[t]);
    case "pointer":
      return f.toPointer(e.path);
    default:
      throw new TypeError("Unknown result type");
  }
};
f.prototype._handleCallback = function(e, t, r) {
  if (t) {
    const n = this._getPreferredOutput(e);
    e.path = typeof e.path == "string" ? e.path : f.toPathString(e.path), t(n, r, e);
  }
};
f.prototype._trace = function(e, t, r, n, i, o, a, c) {
  let l;
  if (!e.length)
    return l = {
      path: r,
      value: t,
      parent: n,
      parentProperty: i,
      hasArrExpr: a
    }, this._handleCallback(l, o, "value"), l;
  const h = e[0], E = e.slice(1), p = [];
  function g(u) {
    Array.isArray(u) ? u.forEach((O) => {
      p.push(O);
    }) : p.push(u);
  }
  if ((typeof h != "string" || c) && t && Object.hasOwn(t, h))
    g(this._trace(E, t[h], A(r, h), t, h, o, a));
  else if (h === "*")
    this._walk(t, (u) => {
      g(this._trace(E, t[u], A(r, u), t, u, o, !0, !0));
    });
  else if (h === "..")
    g(this._trace(E, t, r, n, i, o, a)), this._walk(t, (u) => {
      typeof t[u] == "object" && g(this._trace(e.slice(), t[u], A(r, u), t, u, o, !0));
    });
  else {
    if (h === "^")
      return this._hasParentSelector = !0, {
        path: r.slice(0, -1),
        expr: E,
        isParentSelector: !0
      };
    if (h === "~")
      return l = {
        path: A(r, h),
        value: i,
        parent: n,
        parentProperty: null
      }, this._handleCallback(l, o, "property"), l;
    if (h === "$")
      g(this._trace(E, t, r, null, null, o, a));
    else if (/^(-?\d*):(-?\d*):?(\d*)$/u.test(h))
      g(this._slice(h, E, t, r, n, i, o));
    else if (h.indexOf("?(") === 0) {
      if (this.currEval === !1)
        throw new Error("Eval [?(expr)] prevented in JSONPath expression.");
      const u = h.replace(/^\?\((.*?)\)$/u, "$1"), O = /@.?([^?]*)[['](\??\(.*?\))(?!.\)\])[\]']/gu.exec(u);
      O ? this._walk(t, (b) => {
        const P = [O[2]], T = O[1] ? t[b][O[1]] : t[b];
        this._trace(P, T, r, n, i, o, !0).length > 0 && g(this._trace(E, t[b], A(r, b), t, b, o, !0));
      }) : this._walk(t, (b) => {
        this._eval(u, t[b], b, r, n, i) && g(this._trace(E, t[b], A(r, b), t, b, o, !0));
      });
    } else if (h[0] === "(") {
      if (this.currEval === !1)
        throw new Error("Eval [(expr)] prevented in JSONPath expression.");
      g(this._trace(R(this._eval(h, t, r.at(-1), r.slice(0, -1), n, i), E), t, r, n, i, o, a));
    } else if (h[0] === "@") {
      let u = !1;
      const O = h.slice(1, -2);
      switch (O) {
        case "scalar":
          (!t || !["object", "function"].includes(typeof t)) && (u = !0);
          break;
        case "boolean":
        case "string":
        case "undefined":
        case "function":
          typeof t === O && (u = !0);
          break;
        case "integer":
          Number.isFinite(t) && !(t % 1) && (u = !0);
          break;
        case "number":
          Number.isFinite(t) && (u = !0);
          break;
        case "nonFinite":
          typeof t == "number" && !Number.isFinite(t) && (u = !0);
          break;
        case "object":
          t && typeof t === O && (u = !0);
          break;
        case "array":
          Array.isArray(t) && (u = !0);
          break;
        case "other":
          u = this.currOtherTypeCallback(t, r, n, i);
          break;
        case "null":
          t === null && (u = !0);
          break;
        default:
          throw new TypeError("Unknown value type " + O);
      }
      if (u)
        return l = {
          path: r,
          value: t,
          parent: n,
          parentProperty: i
        }, this._handleCallback(l, o, "value"), l;
    } else if (h[0] === "`" && t && Object.hasOwn(t, h.slice(1))) {
      const u = h.slice(1);
      g(this._trace(E, t[u], A(r, u), t, u, o, a, !0));
    } else if (h.includes(",")) {
      const u = h.split(",");
      for (const O of u)
        g(this._trace(R(O, E), t, r, n, i, o, !0));
    } else !c && t && Object.hasOwn(t, h) && g(this._trace(E, t[h], A(r, h), t, h, o, a, !0));
  }
  if (this._hasParentSelector)
    for (let u = 0; u < p.length; u++) {
      const O = p[u];
      if (O && O.isParentSelector) {
        const b = this._trace(O.expr, t, O.path, n, i, o, a);
        if (Array.isArray(b)) {
          p[u] = b[0];
          const P = b.length;
          for (let T = 1; T < P; T++)
            u++, p.splice(u, 0, b[T]);
        } else
          p[u] = b;
      }
    }
  return p;
};
f.prototype._walk = function(e, t) {
  if (Array.isArray(e)) {
    const r = e.length;
    for (let n = 0; n < r; n++)
      t(n);
  } else e && typeof e == "object" && Object.keys(e).forEach((r) => {
    t(r);
  });
};
f.prototype._slice = function(e, t, r, n, i, o, a) {
  if (!Array.isArray(r))
    return;
  const c = r.length, l = e.split(":"), h = l[2] && Number.parseInt(l[2]) || 1;
  let E = l[0] && Number.parseInt(l[0]) || 0, p = l[1] && Number.parseInt(l[1]) || c;
  E = E < 0 ? Math.max(0, E + c) : Math.min(c, E), p = p < 0 ? Math.max(0, p + c) : Math.min(c, p);
  const g = [];
  for (let u = E; u < p; u += h)
    this._trace(R(u, t), r, n, i, o, a, !0).forEach((b) => {
      g.push(b);
    });
  return g;
};
f.prototype._eval = function(e, t, r, n, i, o) {
  this.currSandbox._$_parentProperty = o, this.currSandbox._$_parent = i, this.currSandbox._$_property = r, this.currSandbox._$_root = this.json, this.currSandbox._$_v = t;
  const a = e.includes("@path");
  a && (this.currSandbox._$_path = f.toPathString(n.concat([r])));
  const c = this.currEval + "Script:" + e;
  if (!f.cache[c]) {
    let l = e.replaceAll("@parentProperty", "_$_parentProperty").replaceAll("@parent", "_$_parent").replaceAll("@property", "_$_property").replaceAll("@root", "_$_root").replaceAll(/@([.\s)[])/gu, "_$_v$1");
    if (a && (l = l.replaceAll("@path", "_$_path")), this.currEval === "safe" || this.currEval === !0 || this.currEval === void 0)
      f.cache[c] = new this.safeVm.Script(l);
    else if (this.currEval === "native")
      f.cache[c] = new this.vm.Script(l);
    else if (typeof this.currEval == "function" && this.currEval.prototype && Object.hasOwn(this.currEval.prototype, "runInNewContext")) {
      const h = this.currEval;
      f.cache[c] = new h(l);
    } else if (typeof this.currEval == "function")
      f.cache[c] = {
        runInNewContext: (h) => this.currEval(l, h)
      };
    else
      throw new TypeError(`Unknown "eval" property "${this.currEval}"`);
  }
  try {
    return f.cache[c].runInNewContext(this.currSandbox);
  } catch (l) {
    if (this.ignoreEvalErrors)
      return !1;
    throw new Error("jsonPath: " + l.message + ": " + e);
  }
};
f.cache = {};
f.toPathString = function(e) {
  const t = e, r = t.length;
  let n = "$";
  for (let i = 1; i < r; i++)
    /^(~|\^|@.*?\(\))$/u.test(t[i]) || (n += /^[0-9*]+$/u.test(t[i]) ? "[" + t[i] + "]" : "['" + t[i] + "']");
  return n;
};
f.toPointer = function(e) {
  const t = e, r = t.length;
  let n = "";
  for (let i = 1; i < r; i++)
    /^(~|\^|@.*?\(\))$/u.test(t[i]) || (n += "/" + t[i].toString().replaceAll("~", "~0").replaceAll("/", "~1"));
  return n;
};
f.toPathArray = function(e) {
  const {
    cache: t
  } = f;
  if (t[e])
    return t[e].concat();
  const r = [], i = e.replaceAll(/@(?:null|boolean|number|string|integer|undefined|nonFinite|scalar|array|object|function|other)\(\)/gu, ";$&;").replaceAll(/[['](\??\(.*?\))[\]'](?!.\])/gu, function(o, a) {
    return "[#" + (r.push(a) - 1) + "]";
  }).replaceAll(/\[['"]([^'\]]*)['"]\]/gu, function(o, a) {
    return "['" + a.replaceAll(".", "%@%").replaceAll("~", "%%@@%%") + "']";
  }).replaceAll("~", ";~;").replaceAll(/['"]?\.['"]?(?![^[]*\])|\[['"]?/gu, ";").replaceAll("%@%", ".").replaceAll("%%@@%%", "~").replaceAll(/(?:;)?(\^+)(?:;)?/gu, function(o, a) {
    return ";" + a.split("").join(";") + ";";
  }).replaceAll(/;;;|;;/gu, ";..;").replaceAll(/;$|'?\]|'$/gu, "").split(";").map(function(o) {
    const a = o.match(/#(\d+)/u);
    return !a || !a[1] ? o : r[a[1]];
  });
  return t[e] = i, t[e].concat();
};
f.prototype.safeVm = {
  Script: G
};
const H = function(e, t, r) {
  const n = e.length;
  for (let i = 0; i < n; i++) {
    const o = e[i];
    r(o) && t.push(e.splice(i--, 1)[0]);
  }
};
class Y {
  /**
   * @param {string} expr Expression to evaluate
   */
  constructor(t) {
    this.code = t;
  }
  /**
   * @param {object} context Object whose items will be added
   *   to evaluation
   * @returns {EvaluatedResult} Result of evaluated code
   */
  runInNewContext(t) {
    let r = this.code;
    const n = Object.keys(t), i = [];
    H(n, i, (h) => typeof t[h] == "function");
    const o = n.map((h) => t[h]);
    r = i.reduce((h, E) => {
      let p = t[E].toString();
      return /function/u.test(p) || (p = "function " + p), "var " + E + "=" + p + ";" + h;
    }, "") + r, !/(['"])use strict\1/u.test(r) && !n.includes("arguments") && (r = "var arguments = undefined;" + r), r = r.replace(/;\s*$/u, "");
    const c = r.lastIndexOf(";"), l = c > -1 ? r.slice(0, c + 1) + " return " + r.slice(c + 1) : " return " + r;
    return new Function(...n, l)(...o);
  }
}
f.prototype.vm = {
  Script: Y
};
var D = /* @__PURE__ */ ((e) => (e.TRANSMITTED = "TRANSMITTED", e.TRANSMITTED_EXTERNAL = "TRANSMITTED_EXTERNAL", e))(D || {}), _ = /* @__PURE__ */ ((e) => (e.PONCTUELLE = "PONCTUELLE", e.PLURIANNUELLE = "PLURIANNUELLE", e))(_ || {}), N = /* @__PURE__ */ ((e) => (e.PARTAGE = "PARTAGE", e.SYNCHRONE = "SYNCHRONE", e))(N || {}), y = /* @__PURE__ */ ((e) => (e.INSTRUCTION = "INSTRUCTION", e.FININSTRUCTION = "FININSTRUCTION", e.MISESOUSRAPPORTPREV = "MISESOUSRAPPORTPREV", e.MISESOUSRAPPORT = "MISESOUSRAPPORT", e.PRETAVOTER = "PRETAVOTER", e.VOTE = "VOTE", e.SOLDE = "SOLDE", e))(y || {});
const Q = (
  // The list of status ordered by advancement
  [
    y.INSTRUCTION,
    y.FININSTRUCTION,
    y.MISESOUSRAPPORTPREV,
    y.MISESOUSRAPPORT,
    y.PRETAVOTER,
    y.VOTE,
    y.SOLDE
  ].reverse()
);
function q(e) {
  return e == null ? void 0 : e.startsWith("/connecteur-aides-v9v8/");
}
const z = {
  REQUESTED: "En cours de saisie",
  REGISTERED: "En attente d'avis préalable",
  TRANSMITTED: "Transmise",
  TRANSMITTED_EXTERNAL: "Transmise",
  WIP_SUPPORTED: "En cours de prise en charge",
  SUPPORTED: "Prise en charge",
  RETURNED: " Rejetée",
  ACCEPTABLE: "Recevable",
  UNACCEPTABLE: "Non recevable",
  VALIDATED: "Validée",
  INSTRUCTION: "En cours d'instruction",
  FININSTRUCTION: "Instruction terminée",
  PRETAVOTER: "Prêt à voter",
  MISESOUSRAPPORT: "Mise sous rapport",
  MISESOUSRAPPORTPREV: "Mise sous rapport prévisionnelle",
  BLOQUE: "Bloquée",
  VOTE: "Votée",
  SOLDE: "Soldée",
  CLOTURE: "Clôturée",
  WAITING_FOR_CERTIFICATE: "En attente d'attestation",
  JUSTIFICATION_REQUESTED: "Justification en cours",
  JUSTIFICATION_JUSTIFIED: "Justifiée",
  JUSTIFICATION_NEED_UPDATE: "Justification à modifier",
  JUSTIFICATION_VALIDATED: "Justifiée",
  JUSTIFICATION_READY_TO_JUSTIFY: "À justifier"
}, W = {
  status: z
};
function Z(e) {
  if (!e.dossiersFinancement) return null;
  const t = e.dossiersFinancement.map((o) => o.statut), n = f({ path: "$..lignes", json: e.planFinancement ?? [] }).flat().filter((o) => {
    var a, c;
    return q((c = (a = o == null ? void 0 : o.financement) == null ? void 0 : a.source) == null ? void 0 : c.href);
  }).map((o) => o.financement.statut), i = [...t, ...n];
  return Q.find((o) => i.includes(o)) ?? null;
}
function J(e, t) {
  return e && (!t || [y.VOTE, y.SOLDE].includes(
    t
  )) ? `JUSTIFICATION_${e}` : null;
}
function tt(e, t, r, n) {
  var o, a, c;
  let i = J(n, t);
  if (i) return i;
  if (t && !r)
    i = t;
  else if (!t && !r && !((o = e.propagation) != null && o.external)) {
    let l = f({
      path: '$..recette..lignes[?(@.reference=="MGS_LIGNE_FICTIVE")]',
      json: e.planFinancement ?? []
    });
    i = ((c = (a = l == null ? void 0 : l[0]) == null ? void 0 : a.financement) == null ? void 0 : c.statut) ?? e.status;
  }
  return i;
}
function at(e = {}) {
  var a, c, l;
  const t = Z(e) ?? "", r = (a = e.cloture) == null ? void 0 : a.value, n = e.multiFinanceur, i = (c = e.justification) == null ? void 0 : c.statut;
  let o;
  return r ? o = "CLOTURE" : (o = tt(
    e,
    t,
    n,
    i
  ), o || (e.achievements && Array.isArray(e.achievements.items) && e.achievements.items.some(({ done: h }) => h) ? o = `WIP_${e.achievements.to}` : o = e.status)), (l = e.propagation) != null && l.external && [o, e.status].includes(D.TRANSMITTED) && (o = D.TRANSMITTED_EXTERNAL), o ?? null;
}
function lt(e) {
  return W.status[e] ?? null;
}
var U = /* @__PURE__ */ ((e) => (e.PARTAGE = "PARTAGE", e.SYNCHRONE = "SYNCHRONE", e))(U || {}), L = /* @__PURE__ */ ((e) => (e.ANNEE_OU_INTERVALLE_TEMPOREL = "ANNEE_OU_INTERVALLE_TEMPOREL", e.ANNEE_SEULE = "ANNEE_SEULE", e))(L || {});
const S = "[demande-financement:plan-financement]";
function et(e, t) {
  var o, a, c, l, h;
  if (e.idCpo) return !0;
  const r = ((a = (o = t.multiFinanceur) == null ? void 0 : o.options) == null ? void 0 : a.modePreInstruction) === U.PARTAGE, n = e.periode === _.PLURIANNUELLE, i = ((h = (l = (c = t.workflow) == null ? void 0 : c.simple) == null ? void 0 : l.pageDocumentComptable) == null ? void 0 : h.affichageMillesime) === L.ANNEE_SEULE;
  return n && r && i;
}
function ht(e, t) {
  var n, i;
  let r;
  return et(e, t) && (r = (n = e.planFinancement) == null ? void 0 : n.find(
    ({ periode: o }) => (o == null ? void 0 : o.exercice) === e.exerciceBudgetaire
  )), r ?? ((i = e.planFinancement) == null ? void 0 : i[0]);
}
function rt(e) {
  if (!e) throw new Error(`${S} - Missing demandeFinancement parameter`);
  const t = e.planFinancement;
  return t != null && t[0] ? t.find(
    (n) => {
      var i;
      return ((i = n.periode) == null ? void 0 : i.exercice) === e.exerciceBudgetaire;
    }
  ) ?? t[0] : null;
}
function ct(e, t) {
  if (!e) throw new Error(`${S} - Missing demandeFinancement parameter`);
  if (!t) throw new Error(`${S} - Missing financeurId parameter`);
  if (e.multiFinanceur) {
    const r = rt(e);
    return f({ path: "$.recette..lignes[*]", json: r ?? {} }).find((i) => {
      var o, a;
      return ((a = (o = i.financement) == null ? void 0 : o.financeur) == null ? void 0 : a.href) === t;
    }) ?? null;
  }
  return null;
}
function ut(e, t) {
  if (!e) throw new Error(`${S} - Missing demandeFinancement parameter`);
  if (!t) throw new Error(`${S} - Missing dossierId parameter`);
  return f({ path: "$.planFinancement[*].recette..lignes[*]", json: e }).find((n) => {
    var i, o;
    return ((o = (i = n.financement) == null ? void 0 : i.source) == null ? void 0 : o.href) === t;
  });
}
const ft = (e) => {
  var t, r, n, i;
  return ((i = (n = (r = (t = e.recette) == null ? void 0 : t.postes) == null ? void 0 : r.find(({ reference: o }) => o === "MGS_POSTE_FICTIF")) == null ? void 0 : n.lignes) == null ? void 0 : i.find(({ reference: o }) => o === "MGS_LIGNE_FICTIVE")) ?? null;
}, I = "[demande-financement:demande-financement]";
function it(e, t) {
  var r;
  return (r = e.organisation) != null && r.id && t.organizationUnit ? e.organisation.id === t.organizationUnit : !1;
}
function nt(e, t) {
  var r, n, i;
  return (n = (r = e.groupesGestion) == null ? void 0 : r[0]) != null && n.href ? e.groupesGestion[0].href.split("/").pop() === ((i = t.groupsGestion) == null ? void 0 : i[0]) : !1;
}
function Et(e, t, r) {
  var o;
  if (!e) throw new Error(`${I} - Missing demandeFinancement parameter`);
  if (!t) throw new Error(`${I} - Missing teleservice parameter`);
  if (!r) throw new Error(`${I} - Missing currentUser parameter`);
  const n = (o = e.multiFinancement) == null ? void 0 : o.financeurs, i = n == null ? void 0 : n.filter((a) => {
    var c;
    return (c = a.source) == null ? void 0 : c.href;
  });
  return i == null ? void 0 : i.find((a) => {
    var c, l, h;
    return !((c = t.multiFinanceur) != null && c.active) || e.financeurPrincipal != null ? !1 : ((l = e.optionsMultiFinanceur) == null ? void 0 : l.modePreInstruction) === N.SYNCHRONE ? it(a, r) : ((h = e.optionsMultiFinanceur) == null ? void 0 : h.modePreInstruction) === N.PARTAGE ? nt(a, r) : !1;
  });
}
export {
  at as buildVirtualStatus,
  rt as extractPlanFinancementFromDemande,
  ft as findLigneFictive,
  ht as getCurrentPlanFinancement,
  Et as getFinanceurFromDemande,
  ut as getLigneOfDossier,
  ct as getLigneOfFinanceur,
  et as isCPO,
  lt as translateVirtualStatus
};
//# sourceMappingURL=demande-financement-utils.es.js.map
